import { isPdf, useIntl } from '@capsicum/common';
import { Alert, Divider } from '@capsicum/common-web';
import { AlertIcon, DocumentIcon, EditIcon, PdfIcon, TrashIcon } from '@capsicum/common-web/icons';
import { type SubmissionStepProps } from '@capsicum/features/regulations/shared';
import styled from 'styled-components';

export const SubmissionStep = ({
	isMaxFilesAmountExceeded,
	maxFilesAmount,
	navigateToCategory,
	onDeleteAllDocuments,
	submissionList
}: SubmissionStepProps) => {
	const { $t } = useIntl();

	return (
		<>
			<S.CategoryHeader>
				<S.Title>{$t('sourceOfIncome.submission.title')}</S.Title>
				<S.Subtitle $isMaxFilesAmountExceeded={isMaxFilesAmountExceeded}>
					{$t('sourceOfIncome.submission.description')}
				</S.Subtitle>
				{isMaxFilesAmountExceeded && (
					<Alert
						closable={false}
						icon={AlertIcon}
						subTitle={$t('sourceOfIncome.submission.errorText', { numberOfDocuments: maxFilesAmount })}
						testID="source-of-income-submission-error"
						title={$t('sourceOfIncome.submission.errorTitle')}
						type="warning"
					/>
				)}
			</S.CategoryHeader>
			{Object.entries(submissionList).map(([categoryId, { requirementImages, title }], index, array) => (
				<S.Category key={categoryId}>
					<S.RequirementHeader>
						<S.RequirementName>{title}</S.RequirementName>
						<S.RequirementHeaderButtons>
							<S.EditButton
								data-testid={`source-of-income-navigate-to-category-${categoryId}`}
								height={20}
								width={20}
								onClick={() => navigateToCategory(categoryId)}
							/>
							{requirementImages.length > 0 && (
								<S.RemoveButton
									data-testid={`source-of-income-delete-all-documents-to-${categoryId}`}
									height={20}
									width={20}
									onClick={() => onDeleteAllDocuments(categoryId)}
								/>
							)}
						</S.RequirementHeaderButtons>
					</S.RequirementHeader>
					{requirementImages.map(image => (
						<S.FileRow key={image.id}>
							{isPdf(image) ? (
								<PdfIcon height={24} width={24} />
							) : (
								<DocumentIcon height={24} width={24} />
							)}
							<S.FileName>{image.name}</S.FileName>
						</S.FileRow>
					))}
					{index < array.length - 1 && <Divider orientation="horizontal" />}
				</S.Category>
			))}
		</>
	);
};

namespace S {
	export const Title = styled.span`
		display: block;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const Subtitle = styled.span<{ $isMaxFilesAmountExceeded: boolean }>`
		display: block;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text3};
		${({ $isMaxFilesAmountExceeded, theme }) =>
			$isMaxFilesAmountExceeded && `margin-bottom: ${theme.spacings.small}`};
	`;

	export const Category = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const CategoryHeader = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.large};
	`;

	export const RequirementHeader = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	`;

	export const RequirementHeaderButtons = styled.div`
		display: flex;
		justify-content: flex-end;
		align-items: flex-start;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EditButton = styled(EditIcon)`
		cursor: pointer;
	`;

	export const RemoveButton = styled(TrashIcon)`
		cursor: pointer;
	`;

	export const RequirementName = styled.div`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const FileRow = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const FileName = styled.span`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		${({ theme }) => theme.fonts.text.text3};
	`;
}
